import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { I18nProvider } from "@/context/I18nContext";
import { HelpProvider } from "@/context/HelpContext";
import Layout from "@/components/Layout";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import MunicipalityDashboard from "@/pages/MunicipalityDashboard";
import PoolOwnerDashboard from "@/pages/PoolOwnerDashboard";
import { PendingAccountNotice } from "@/components/PendingAccountNotice";
import UtilityDashboard from "@/pages/UtilityDashboard";
import AuthCallback from "@/pages/AuthCallback";
import Onboarding from "@/pages/Onboarding";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import { BlogList, BlogPost } from "@/pages/Blog";
import SuperAdminDashboard from "@/pages/SuperAdmin";
import { Datenschutz, Impressum } from "@/pages/Legal";

function AppRoot() {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="grid min-h-[60vh] place-items-center text-muted-foreground">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!user.role) return <Onboarding />;
  if (user.role === "municipality") return <MunicipalityDashboard />;
  if (user.role === "utility") return <UtilityDashboard />;
  if ((user.account_status || "active") !== "active") return <PendingAccountNotice />;
  return <PoolOwnerDashboard />;
}

function LandingGate() {
  const { user } = useAuth();
  if (user === null) return null;
  if (user) return <Navigate to="/app" replace />;
  return <Landing />;
}

function AuthedRedirect({ children }) {
  const { user } = useAuth();
  if (user) return <Navigate to="/app" replace />;
  return children;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// Synchronous session_id detection — must run before Routes to avoid race conditions.
function RootRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<LandingGate />} />
      <Route
        path="/login"
        element={
          <AuthedRedirect>
            <Login />
          </AuthedRedirect>
        }
      />
      <Route
        path="/register"
        element={
          <AuthedRedirect>
            <Register />
          </AuthedRedirect>
        }
      />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/blog" element={<BlogList />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/datenschutz" element={<Datenschutz />} />
      <Route path="/impressum" element={<Impressum />} />
      <Route path="/admin" element={<SuperAdminDashboard />} />
      <Route path="/app" element={<AppRoot />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          <HelpProvider>
            <ScrollToTop />
            <Layout>
              <RootRouter />
            </Layout>
            <Toaster richColors position="top-right" />
          </HelpProvider>
        </BrowserRouter>
      </AuthProvider>
    </I18nProvider>
  );
}
